import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { DockGlyph } from './dock-icons';
import type { Quest, QuestProgressData } from './bit-brew-constants';

interface QuestCarouselProps {
  quests: Quest[];
  progressData: QuestProgressData;
  completedQuests: number[];
  currencyLabel?: string; // per-shop points word (040); default BEANS
  embedded?: boolean; // kept for prop-surface stability — the PASS tab supplies its own layout now
}

export default function QuestCarousel({ quests, progressData, completedQuests, currencyLabel }: QuestCarouselProps) {
  const [page, setPage] = useState(0);
  const [expandedQuest, setExpandedQuest] = useState<Quest | null>(null);
  const questsPerPage = 2;
  const totalPages = Math.ceil(quests.length / questsPerPage);

  const pageQuests = quests.slice(page * questsPerPage, (page + 1) * questsPerPage);

  return (
    <div className="flex flex-col gap-3">
      {/* Header with nav */}
      <div className="flex justify-between items-center">
        <span className="font-heading text-[9px] tracking-[1px] uppercase text-body-grey flex items-center gap-2">
          <DockGlyph icon="quests" className="w-3 h-3" /> QUESTS
        </span>
        <div className="flex items-center gap-2 pixel-corners-sm bg-black/[.18] text-white font-grotesk font-bold px-3 py-2">
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="disabled:opacity-30"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="font-heading text-[8px]">
            {page + 1}/{totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page === totalPages - 1}
            className="disabled:opacity-30"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Quest cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {pageQuests.map((quest) => {
          const progress = progressData[quest.progressKey] || 0;
          const isComplete = completedQuests.includes(quest.id);
          const percent = Math.min(100, (progress / quest.target) * 100);

          return (
            <div
              key={quest.id}
              onClick={() => setExpandedQuest(quest)}
              className={`bg-white pixel-corners p-4 flex flex-col cursor-pointer transition-colors ${
                isComplete ? 'ring-2 ring-success' : ''
              }`}
            >
              <div className="flex justify-between items-baseline gap-[10px] mb-2">
                <span className="font-grotesk font-bold text-[16px] text-primary">
                  {quest.name}
                </span>
                <span className="font-heading text-[9px] text-coral shrink-0 flex items-center gap-1">
                  <DockGlyph icon="bean" className="w-3 h-3" /> {quest.reward}
                </span>
              </div>
              <p className="font-grotesk font-medium text-[13px] text-body-grey mb-[10px]">{quest.desc}</p>

              <div className="h-2 pixel-bar pixel-corners-sm [--bar-gap:#f2ead6] bg-tab-inactive overflow-hidden mt-auto">
                <motion.div
                  className={`h-full ${isComplete ? 'bg-success' : 'bg-pixel-cyan'}`}
                  initial={{ width: 0 }}
                  animate={{ width: `${percent}%` }}
                />
              </div>
              <div className="flex justify-between items-center mt-2">
                <span className="font-heading text-[7px] text-body-grey/70">TAP FOR DETAILS</span>
                <span className="font-body text-[11px] text-body-grey">
                  {Math.min(progress, quest.target) + (quest.displayOffset ?? 0)}/{quest.target + (quest.displayOffset ?? 0)}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* ═══ Expanded Quest Modal ═══ */}
      <AnimatePresence>
        {expandedQuest && (() => {
          const progress = progressData[expandedQuest.progressKey] || 0;
          const isComplete = completedQuests.includes(expandedQuest.id);
          const percent = Math.min(100, (progress / expandedQuest.target) * 100);

          return (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center safe-modal bg-black/70 backdrop-blur-sm"
              onClick={() => setExpandedQuest(null)}
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                className="w-full max-w-md bg-white pixel-corners border-[3px] border-primary p-5 sm:p-8 relative max-h-full overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Close button */}
                <button
                  onClick={() => setExpandedQuest(null)}
                  className="absolute top-1 right-1 p-3 text-body-grey hover:text-primary transition-colors"
                >
                  <X size={20} />
                </button>

                {/* Status badge */}
                <div className="mb-4">
                  <span className={`font-heading text-[9px] pixel-corners-sm px-3 py-1.5 ${
                    isComplete ? 'bg-success text-white' : 'bg-tab-inactive text-body-grey'
                  }`}>
                    {isComplete ? 'COMPLETED' : 'IN PROGRESS'}
                  </span>
                </div>

                {/* Quest name */}
                <h3 className="font-grotesk font-bold text-[22px] text-primary mb-4 leading-tight">
                  {expandedQuest.name}
                </h3>

                {/* Description — readable size */}
                <p className="font-grotesk font-medium text-[15px] text-body-grey leading-relaxed mb-6">
                  {expandedQuest.desc}
                </p>

                {/* Progress bar */}
                <div className="mb-2">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-heading text-[9px] text-body-grey">PROGRESS</span>
                    <span className="font-heading text-[9px] text-body-grey">
                      {Math.min(progress, expandedQuest.target) + (expandedQuest.displayOffset ?? 0)} / {expandedQuest.target + (expandedQuest.displayOffset ?? 0)}
                    </span>
                  </div>
                  <div className="h-3 pixel-bar pixel-corners-sm [--bar-gap:#f2ead6] bg-tab-inactive overflow-hidden">
                    <motion.div
                      className={`h-full ${isComplete ? 'bg-success' : 'bg-pixel-cyan'}`}
                      initial={{ width: 0 }}
                      animate={{ width: `${percent}%` }}
                      transition={{ duration: 0.6, ease: 'easeOut' }}
                    />
                  </div>
                </div>

                {/* Reward */}
                <div className="mt-6 pt-4 border-t border-hairline flex items-center justify-between">
                  <span className="font-heading text-[9px] text-body-grey">REWARD</span>
                  <div className="flex items-center gap-2 text-coral font-grotesk font-bold text-[16px]">
                    <DockGlyph icon="bean" className="w-4 h-4" /> {expandedQuest.reward} {currencyLabel || 'BEANS'}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          );
        })()}
      </AnimatePresence>
    </div>
  );
}
